import React, { useState, useEffect } from "react";
import { FaPython, FaJs, FaHtml5, FaCss3Alt, FaReact, FaJava, FaCode } from "react-icons/fa";
import { SiTailwindcss } from "react-icons/si";

const tecnologias = [
    {
      name: "Python",
      icon: <FaPython className="w-8 h-8 md:w-10 md:h-10" />,
      color: "from-blue-400 to-yellow-400",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/20",
      glowColor: "shadow-blue-500/20",
      category: "backend",
      experience: { years: 2, months: 5 },
      description: {
        es: "Desarrollo backend, APIs y análisis de datos",
        en: "Backend development, APIs and data analysis"
      }
    },
    {
      name: "JavaScript",
      icon: <FaJs className="w-8 h-8 md:w-10 md:h-10" />,
      color: "from-yellow-400 to-orange-400",
      bgColor: "bg-yellow-500/10",
      borderColor: "border-yellow-500/20",
      glowColor: "shadow-yellow-500/20",
      category: "frontend",
      experience: { years: 2, months: 6 },
      description: {
        es: "Desarrollo frontend interactivo y dinámico",
        en: "Interactive and dynamic frontend development"
      }
    },
    {
      name: "React",
      icon: <FaReact className="w-8 h-8 md:w-10 md:h-10" />,
      color: "from-blue-300 to-cyan-300",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/20",
      glowColor: "shadow-blue-500/20",
      category: "frontend",
      experience: { years: 2, months: 8 },
      description: {
        es: "Aplicaciones web modernas y componentes reutilizables",
        en: "Modern web applications and reusable components"
      }
    },
    {
      name: "HTML5",
      icon: <FaHtml5 className="w-8 h-8 md:w-10 md:h-10" />,
      color: "from-orange-400 to-red-400",
      bgColor: "bg-orange-500/10",
      borderColor: "border-orange-500/20",
      glowColor: "shadow-orange-500/20",
      category: "frontend",
      experience: { years: 2, months: 0 },
      description: {
        es: "Estructura semántica y accesible",
        en: "Semantic and accessible structure"
      }
    },
    {
      name: "CSS3",
      icon: <FaCss3Alt className="w-8 h-8 md:w-10 md:h-10" />,
      color: "from-blue-400 to-blue-600",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/20",
      glowColor: "shadow-blue-500/20",
      category: "frontend",
      experience: { years: 2, months: 0 },
      description: {
        es: "Estilos modernos y animaciones fluidas",
        en: "Modern styles and smooth animations"
      }
    },
    {
      name: "Tailwind CSS",
      icon: <SiTailwindcss className="w-8 h-8 md:w-10 md:h-10" />,
      color: "from-teal-400 to-blue-400",
      bgColor: "bg-teal-500/10",
      borderColor: "border-teal-500/20",
      glowColor: "shadow-teal-500/20",
      category: "frontend",
      experience: { years: 1, months: 6 },
      description: {
        es: "Framework CSS utilitario para desarrollo rápido",
        en: "Utility-first CSS framework for rapid development"
      }
    },
    {
      name: "Java",
      icon: <FaJava className="w-8 h-8 md:w-10 md:h-10" />,
      color: "from-red-500 to-orange-500",
      bgColor: "bg-red-500/10",
      borderColor: "border-red-500/20",
      glowColor: "shadow-red-500/20",
      category: "backend",
      experience: { years: 1, months: 0 },
      description: {
        es: "Desarrollo de aplicaciones empresariales",
        en: "Enterprise application development"
      }
    },
    {
      name: "C#",
      icon: <FaCode className="w-8 h-8 md:w-10 md:h-10" />,
      color: "from-purple-400 to-pink-400",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/20",
      glowColor: "shadow-purple-500/20",
      category: "backend",
      experience: { years: 1, months: 8 },
      description: {
        es: "Desarrollo .NET y aplicaciones de escritorio",
        en: ".NET development and desktop applications"
      }
    },
];

const TecnologiasSection = ({ language }) => {
  const [visibleCards, setVisibleCards] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);

  const translations = {
    es: {
      title: "Tecnologías que manejo",
      subtitle: "Herramientas y lenguajes que domino",
      frontend: "Frontend",
      backend: "Backend",
      tools: "Herramientas",
      experience: "experiencia",
      years: "años",
      months: "meses",
    },
    en: {
      title: "Technologies I use",
      subtitle: "Tools and languages I master",
      frontend: "Frontend",
      backend: "Backend",
      tools: "Tools",
      experience: "experience",
      years: "years",
      months: "months",
    },
  };

  // Animación de aparición escalonada
  useEffect(() => {
    const timer = setTimeout(() => {
      tecnologias.forEach((_, index) => {
        setTimeout(() => {
          setVisibleCards(prev => [...prev, index]);
        }, index * 100);
      });
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  const getExperienceText = (experience) => {
    const { years, months } = experience;
    const t = translations[language];

    if (years > 0) {
      return `${years} ${t.years} ${t.experience}`;
    } else {
      return `${months} ${t.months} ${t.experience}`;
    }
  };

  const categories = {
    frontend: tecnologias.filter(tech => tech.category === "frontend"),
    backend: tecnologias.filter(tech => tech.category === "backend"),
  };

  return (
    <div className="flex flex-col items-center justify-center bg-black text-white min-h-screen py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-jersey mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
            {translations[language].title}
          </h2>
          <p className="text-lg md:text-xl text-gray-300 font-paragraph max-w-2xl mx-auto">
            {translations[language].subtitle}
          </p>
          <div className="mt-6 h-1 w-24 bg-gradient-to-r from-teal-400 to-blue-400 mx-auto rounded-full" />
        </div>

        {/* Technologies Grid */}
        <div className="space-y-16">
          {Object.entries(categories).map(([categoryKey, techs]) => (
            <div key={categoryKey} className="space-y-8">
              <h3 className="text-2xl md:text-3xl font-jersey text-center text-teal-400 uppercase tracking-wider">
                {translations[language][categoryKey]}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {techs.map((tech, index) => {
                  const globalIndex = tecnologias.findIndex(t => t.name === tech.name);
                  const isVisible = visibleCards.includes(globalIndex);
                  const isHovered = hoveredCard === globalIndex;

                  return (
                    <div
                      key={tech.name}
                      className={`group relative transform transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                        }`}
                      onMouseEnter={() => setHoveredCard(globalIndex)}
                      onMouseLeave={() => setHoveredCard(null)}
                    >
                      {/* Card */}
                      <div className={`
                        relative p-6 rounded-2xl border backdrop-blur-sm transition-all duration-500
                        ${tech.bgColor} ${tech.borderColor}
                        ${isHovered ? `${tech.glowColor} shadow-2xl scale-105 -translate-y-2` : 'shadow-lg'}
                        hover:border-opacity-50 cursor-pointer
                      `}>
                        {/* Gradient overlay */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${tech.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-500`} />

                        {/* Content */}
                        <div className="relative z-10">
                          {/* Icon */}
                          <div className={`text-gray-300 group-hover:text-white mb-4 transition-all duration-300 ${isHovered ? 'scale-110' : ''
                            }`}>
                            {tech.icon}
                          </div>

                          {/* Name */}
                          <h4 className="text-xl font-paragraph font-semibold mb-2 group-hover:text-white transition-colors duration-300">
                            {tech.name}
                          </h4>

                          {/* Experience */}
                          <div className="text-sm text-gray-400 mb-3">
                            {getExperienceText(tech.experience)}
                          </div>

                          {/* Description */}
                          <p className="text-sm text-gray-500 group-hover:text-gray-300 transition-colors duration-300 line-clamp-2">
                            {tech.description[language]}
                          </p>
                        </div>

                        {/* Hover effect border */}
                        <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-gradient-to-r group-hover:from-teal-400/50 group-hover:to-blue-400/50 transition-all duration-500" />
                      </div>

                      {/* Floating effect */}
                      <div className={`absolute -inset-1 bg-gradient-to-r ${tech.color} rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500 -z-10`} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-20 text-center">
          <div className="inline-flex items-center space-x-8 bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50">
            <div className="text-center">
              <div className="text-3xl font-jersey text-teal-400 mb-1">
                {tecnologias.length}
              </div>
              <div className="text-sm text-gray-400">
                {language === 'es' ? 'Tecnologías' : 'Technologies'}
              </div>
            </div>
            <div className="w-px h-12 bg-gray-600" />
            <div className="text-center">
              <div className="text-3xl font-jersey text-blue-400 mb-1">
                {Math.max(...tecnologias.map(t => t.experience.years))}+
              </div>
              <div className="text-sm text-gray-400">
                {language === 'es' ? 'Años de experiencia' : 'Years of experience'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TecnologiasSection;